/**
 * One-time banner for legacy users who registered with a password before
 * the passwordless redesign. Renders only when the authenticated user has
 * ``passwordless_migration_acknowledged_at === null``. Dismissing the banner
 * calls ``POST /api/auth/passwordless-migration/acknowledge`` and optimistically
 * updates the in-memory user so subsequent navigations don't flicker the
 * banner back in while the next ``/auth/me`` is in flight.
 *
 * The banner is intentionally non-blocking: a thin sticky strip across the
 * top of the page, a single short paragraph, and a "Got it" affordance.
 * No modal, no overlay — we don't want to interrupt a user mid-task just
 * to tell them passwords are gone.
 */
import React, { useEffect, useState } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from '@/hooks/useTranslation';
import { authAPI } from '@/services/api';
import { brand } from '../../design-system/theme/tokens';

const COLORS = {
  bg: '#EEF2FF', // light indigo
  border: '#C7D2FE',
  iconBg: '#4F46E5',
  text: '#1E293B',
  textMuted: '#475569',
  primary: brand.primary,
};

export default function PasswordlessMigrationBanner() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [hidden, setHidden] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  // Only legacy users see this. ``passwordless_migration_acknowledged_at``
  // is set on user-creation for everyone going forward, so it's only NULL
  // for accounts that came in via the old /api/auth/register path.
  const shouldShow =
    !!user &&
    !hidden &&
    !user.passwordless_migration_acknowledged_at;

  // Auto-hide if the user changes (e.g. after a logout/login switch).
  useEffect(() => {
    setHidden(false);
  }, [user?.id]);

  if (!shouldShow) return null;

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    // Optimistic update: hide instantly so the user gets immediate feedback.
    setHidden(true);
    if (user && setUser) {
      setUser({
        ...user,
        passwordless_migration_acknowledged_at: new Date().toISOString(),
      } as any);
    }
    try {
      await authAPI.acknowledgePasswordlessMigration();
    } catch {
      // If the server rejects (rate limit, network), restore the banner
      // so the user can dismiss it later — don't silently lose state.
      setHidden(false);
      setDismissing(false);
    }
  };

  return (
    <View
      style={{
        backgroundColor: COLORS.bg,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
        paddingHorizontal: 20,
        paddingVertical: 12,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        // Sticky on web so it stays visible as the user scrolls into the app.
        ...(Platform.OS === 'web'
          ? ({ position: 'sticky' as any, top: 0, zIndex: 40 } as any)
          : {}),
      }}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: 16,
          backgroundColor: COLORS.iconBg,
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <MaterialIcons name="lock-open" size={18} color="white" />
      </View>

      <View style={{ flex: 1, flexShrink: 1 }}>
        <Text
          style={{
            color: COLORS.text,
            fontWeight: '600',
            fontSize: 14,
            marginBottom: 2,
          }}
        >
          {t('auth.passwordless.migrationBanner.title')}
        </Text>
        <Text style={{ color: COLORS.textMuted, fontSize: 13, lineHeight: 18 }}>
          {t('auth.passwordless.migrationBanner.body')}
        </Text>
      </View>

      <TouchableOpacity
        onPress={dismiss}
        disabled={dismissing}
        accessibilityRole="button"
        accessibilityLabel={t('auth.passwordless.migrationBanner.gotIt')}
        style={{
          paddingHorizontal: 14,
          paddingVertical: 8,
          borderRadius: 8,
          backgroundColor: COLORS.primary,
          flexShrink: 0,
          opacity: dismissing ? 0.6 : 1,
        }}
      >
        <Text style={{ color: 'white', fontWeight: '600', fontSize: 13 }}>
          {t('auth.passwordless.migrationBanner.gotIt')}
        </Text>
      </TouchableOpacity>
    </View>
  );
}
